import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import lambdaFetch from '../functions/FetchFromLambda';

const { REACT_APP_STAGE } = process.env;

// Get all available timezones using Intl API
const getAllTimezones = () => {
  try {
    if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (error) {
    console.error('Error getting timezones from Intl API:', error);
  }
  
  // Fallback to common timezones if Intl.supportedValuesOf is not available
  return [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'America/Phoenix',
    'America/Toronto',
    'America/Vancouver',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
    'UTC',
  ];
};

export default function SystemSettings(props) {
  const { credentials } = props.fetchInitialData;
  const [timezone, setTimezone] = useState('America/New_York'); // Default to ET
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [timezoneItemExists, setTimezoneItemExists] = useState(false);
  const [currentObjId, setCurrentObjId] = useState(null);

  // Session timeout state
  const [sessionTimeoutRaw, setSessionTimeoutRaw] = useState('0');
  const [sessionTimeoutExists, setSessionTimeoutExists] = useState(false);
  const [sessionTimeoutObjId, setSessionTimeoutObjId] = useState(null);
  const [sessionTimeoutError, setSessionTimeoutError] = useState('');
  const [savingTimeout, setSavingTimeout] = useState(false);

  // Get all available timezones
  const allTimezones = useMemo(() => getAllTimezones(), []);

  // Fetch current timezone setting
  const fetchTimezone = async () => {
    setLoading(true);
    setError(null);

    try {
      // Scan the client-values table for timezone setting
      const endpoint = 'dynamo/client-values/scan';
      const requestBody = {
        limit: 1000,
        filterExpression: '#key = :key',
        expressionAttributeNames: {
          '#key': 'R_KEY',
        },
        expressionAttributeValues: {
          ':key': 'timezone',
        },
      };

      const response = await lambdaFetch(
        endpoint,
        'post',
        credentials.user.accessToken,
        JSON.stringify(requestBody),
        '',
        credentials
      );

      if (response && response.data) {
        let items = [];

        if (Array.isArray(response.data)) {
          items = response.data;
        } else if (response.data.body) {
          let bodyObj = response.data.body;
          if (typeof response.data.body === 'string') {
            bodyObj = JSON.parse(response.data.body);
          }
          let finalData = bodyObj?.data;
          if (typeof bodyObj?.data === 'string') {
            finalData = JSON.parse(bodyObj.data);
          }
          items = Array.isArray(finalData) ? finalData : finalData ? [finalData] : [];
        } else if (response.data.data) {
          items = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        }

        const timezoneItem = items.find(item => item.R_KEY === 'timezone');

        if (timezoneItem) {
          setTimezone(timezoneItem.value || timezoneItem.R_VALUE || 'America/New_York');
          setCurrentObjId(timezoneItem.OBJ_ID);
          setTimezoneItemExists(true);
        } else {
          setTimezone('America/New_York');
          setTimezoneItemExists(false);
          setCurrentObjId(null);
        }
      }
    } catch (err) {
      console.error('Error fetching timezone:', err);
      setError(`Failed to load timezone setting: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Save timezone setting
  const handleSave = async () => {
    setSaving(true);

    try {
      let response;

      if (timezoneItemExists && currentObjId) {
        // Update existing timezone setting
        const endpoint = `dynamo/client-values/item/${currentObjId}/timezone`;
        const updates = {
          value: timezone,
          R_VALUE: timezone,
          description: 'System timezone setting',
          R_DESCRIPTION: 'System timezone setting',
          active: true,
          R_ACTIVE: 1,
        };

        response = await lambdaFetch(
          endpoint,
          'put',
          credentials.user.accessToken,
          JSON.stringify(updates),
          'Timezone updated successfully',
          credentials
        );
      } else {
        // Create new timezone setting
        const endpoint = 'dynamo/client-values';
        const newItem = {
          OBJ_ID: `TIMEZONE_${Date.now()}`,
          R_KEY: 'timezone',
          value: timezone,
          R_VALUE: timezone,
          description: 'System timezone setting',
          R_DESCRIPTION: 'System timezone setting',
          active: true,
          R_ACTIVE: 1,
          DELETE_FLAG: 0,
        };

        response = await lambdaFetch(
          endpoint,
          'post',
          credentials.user.accessToken,
          JSON.stringify(newItem),
          'Timezone created successfully',
          credentials
        );

        if (response && response.success) {
          setTimezoneItemExists(true);
          setCurrentObjId(newItem.OBJ_ID);
        }
      }

      if (response && response.success) {
        setSnackbarMessage('Timezone setting saved successfully! Page will reload in 2 seconds...');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Refresh to confirm the save
        await fetchTimezone();
        
        // Save current tab to sessionStorage so we can return to it after reload
        sessionStorage.setItem('adminTabAfterReload', 'system-settings');
        
        // Reload the page after 2 seconds to apply new timezone throughout the app
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        throw new Error('Failed to save timezone setting');
      }
    } catch (err) {
      console.error('Error saving timezone:', err);
      setSnackbarMessage(`Failed to save timezone: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const validateSessionTimeout = (val) => {
    const n = parseInt(val, 10);
    if (isNaN(n) || !Number.isInteger(n)) return 'Must be a whole number';
    if (n < 0 || n > 480) return 'Must be 0–480 minutes (0 = disabled)';
    return '';
  };

  const fetchSessionTimeout = async () => {
    try {
      const endpoint = 'dynamo/client-values/scan';
      const requestBody = {
        limit: 1000,
        filterExpression: '#key = :key',
        expressionAttributeNames: { '#key': 'R_KEY' },
        expressionAttributeValues: { ':key': 'session_timeout' },
      };

      const response = await lambdaFetch(
        endpoint,
        'post',
        credentials.user.accessToken,
        JSON.stringify(requestBody),
        '',
        credentials
      );

      if (response && response.data) {
        let items = [];
        if (Array.isArray(response.data)) {
          items = response.data;
        } else if (response.data.body) {
          let bodyObj = response.data.body;
          if (typeof response.data.body === 'string') bodyObj = JSON.parse(response.data.body);
          let finalData = bodyObj?.data;
          if (typeof bodyObj?.data === 'string') finalData = JSON.parse(bodyObj.data);
          items = Array.isArray(finalData) ? finalData : finalData ? [finalData] : [];
        } else if (response.data.data) {
          items = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        }

        const item = items.find(i => i.R_KEY === 'session_timeout');
        if (item) {
          const val = parseInt(item.value || item.R_VALUE || '0', 10);
          setSessionTimeoutRaw(String(isNaN(val) ? 0 : val));
          setSessionTimeoutExists(true);
          setSessionTimeoutObjId(item.OBJ_ID);
        } else {
          setSessionTimeoutRaw('0');
          setSessionTimeoutExists(false);
          setSessionTimeoutObjId(null);
        }
      }
    } catch (err) {
      console.error('Error fetching session timeout:', err);
    }
  };

  const handleSaveSessionTimeout = async () => {
    const validationError = validateSessionTimeout(sessionTimeoutRaw);
    if (validationError) return;

    setSavingTimeout(true);
    try {
      const value = String(parseInt(sessionTimeoutRaw, 10));
      let response;

      if (sessionTimeoutExists && sessionTimeoutObjId) {
        const endpoint = `dynamo/client-values/item/${sessionTimeoutObjId}/session_timeout`;
        response = await lambdaFetch(
          endpoint,
          'put',
          credentials.user.accessToken,
          JSON.stringify({
            value,
            R_VALUE: value,
            description: 'Auto-logout after inactivity (minutes). 0 = disabled.',
            R_DESCRIPTION: 'Auto-logout after inactivity (minutes). 0 = disabled.',
            active: true,
            R_ACTIVE: 1,
          }),
          '',
          credentials
        );
      } else {
        const endpoint = 'dynamo/client-values';
        const newItem = {
          OBJ_ID: `SESSION_TIMEOUT_${Date.now()}`,
          R_KEY: 'session_timeout',
          value,
          R_VALUE: value,
          description: 'Auto-logout after inactivity (minutes). 0 = disabled.',
          R_DESCRIPTION: 'Auto-logout after inactivity (minutes). 0 = disabled.',
          active: true,
          R_ACTIVE: 1,
          DELETE_FLAG: 0,
        };
        response = await lambdaFetch(
          endpoint,
          'post',
          credentials.user.accessToken,
          JSON.stringify(newItem),
          '',
          credentials
        );
        if (response && response.success) {
          setSessionTimeoutExists(true);
          setSessionTimeoutObjId(newItem.OBJ_ID);
        }
      }

      if (response && response.success) {
        setSnackbarMessage(
          value === '0'
            ? 'Session timeout disabled. Takes effect after next login.'
            : `Session timeout set to ${value} minute(s). Takes effect after next login.`
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        await fetchSessionTimeout();
      } else {
        throw new Error('Failed to save session timeout setting');
      }
    } catch (err) {
      console.error('Error saving session timeout:', err);
      setSnackbarMessage(`Failed to save session timeout: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSavingTimeout(false);
    }
  };

  // Load settings on mount
  useEffect(() => {
    fetchTimezone();
    fetchSessionTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          System Settings
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Timezone Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set the system timezone for your application. This affects how dates and times 
                are displayed throughout the application. The timezone setting applies to all users.
              </Typography>

              <Autocomplete
                fullWidth
                value={timezone}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setTimezone(newValue);
                  }
                }}
                options={allTimezones}
                disabled={saving}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="System Timezone"
                    placeholder="Type to search (e.g., New_York, London, Tokyo)..."
                    helperText={`${allTimezones.length} timezones available - Current: ${timezone}`}
                  />
                )}
                sx={{ mb: 3 }}
                ListboxProps={{
                  style: { maxHeight: 400 },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 4 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchTimezone}
                disabled={loading || saving}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={loading || saving}
              >
                {saving ? 'Saving...' : 'Save Timezone'}
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Session Timeout
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Automatically log out users after a period of inactivity. Set to 0 to disable.
                Maximum 480 minutes (8 hours). Any activity in any open browser tab resets the
                timer. Takes effect after next login.
              </Typography>

              <TextField
                label="Inactivity Timeout (minutes)"
                type="number"
                fullWidth
                value={sessionTimeoutRaw}
                onChange={(e) => {
                  setSessionTimeoutRaw(e.target.value);
                  setSessionTimeoutError(validateSessionTimeout(e.target.value));
                }}
                error={!!sessionTimeoutError}
                helperText={
                  sessionTimeoutError ||
                  (sessionTimeoutRaw === '0' || sessionTimeoutRaw === ''
                    ? 'Auto-logout disabled'
                    : `Warning shown 60 seconds before logout at ${sessionTimeoutRaw} minute(s)`)
                }
                inputProps={{ min: 0, max: 480 }}
                disabled={savingTimeout}
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchSessionTimeout}
                  disabled={loading || savingTimeout}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={savingTimeout ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSaveSessionTimeout}
                  disabled={!!sessionTimeoutError || loading || savingTimeout}
                >
                  {savingTimeout ? 'Saving...' : 'Save Session Timeout'}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                System settings are stored in DynamoDB table: apix_{REACT_APP_STAGE}_sys_client_values
              </Alert>
            </Box>
          </>
        )}
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ fontSize: '0.875rem' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
